// Server-verified role lookup. Single source of truth for role-based
// navigation *and* the admin route guard, so the UI and the authorization
// check can never disagree.
import { createServerFn } from "@tanstack/react-start";

import { requireSupabaseAuth } from "@/lib/require-auth";
import type { Database } from "@/integrations/supabase/types";

export type AppRole = Database["public"]["Enums"]["app_role"];

export type MyRoles = {
  userId: string;
  email: string | null;
  roles: AppRole[];
  isAdmin: boolean;
  isStaff: boolean;
  /** Tiene rol administrativo pero la sesión no verificó MFA (aal2). */
  mfaPending: boolean;
  /** Tiene un factor TOTP verificado (consultado en el servidor). */
  hasVerifiedFactor: boolean;
  aal: string | null;
};

export const getMyRoles = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<MyRoles> => {
    const { data, error } = await context.supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", context.userId);
    if (error) throw new Error(error.message);

    const roles = ((data ?? []) as { role: AppRole }[]).map((r) => r.role);
    const isAdmin = roles.includes("owner") || roles.includes("admin");
    const { data: pending } = await (context.supabase as any).rpc("my_staff_mfa_pending");
    const aal = ((context.claims as { aal?: string } | undefined)?.aal as string) ?? null;
    // Estado del factor consultado en la base (no en la sesión local).
    let hasVerifiedFactor = false;
    if (pending === true) {
      const { data: f } = await (context.supabase as any).rpc("my_has_verified_totp");
      hasVerifiedFactor = f === true;
    }
    return {
      userId: context.userId,
      email: (context.claims as { email?: string } | undefined)?.email ?? null,
      roles,
      isAdmin,
      isStaff: roles.some((r) => r !== "user"),
      mfaPending: pending === true,
      hasVerifiedFactor,
      aal,
    };
  });
